import React, { useRef, useEffect, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { MAPBOX_TOKEN, API_BASE_URL } from '../../../../config'

/* ─── WMS proxy ──────────────────────────────────────────────── */

/**
 * Canton geo servers (notably www.geoportal.ch) reject requests whose
 * browser `Origin` is not on their internal allowlist — returning 500
 * instead of the tile. Routing every WMS/WFS call through our backend
 * relay strips the Origin (server-to-server), so they answer normally
 * from any first-party domain. See `controllers/public/wmsProxyController`.
 */
const WMS_PROXY_URL = `${API_BASE_URL}/public/wms`

/** Wrap an external WMS/WFS URL so it goes through the backend relay. */
const proxyWms = (targetUrl: string): string =>
  `${WMS_PROXY_URL}?url=${encodeURIComponent(targetUrl)}`

/* ─── Canton WMS Config Registry ─────────────────────────────── */

interface CantonWmsConfig {
  baseUrl: string
  layers: string
  /** GetFeatureInfo format — default 'application/geo+json' */
  gfiInfoFormat?: string
  /** GetFeatureInfo CRS — default 'EPSG:4326'. Use 'EPSG:3857' or 'EPSG:2056' for servers that reject 4326. */
  gfiCrs?: 'EPSG:4326' | 'EPSG:3857' | 'EPSG:2056'
  /** Override WMS base URL for GetFeatureInfo (when tiles and GFI come from different servers) */
  gfiBaseUrl?: string
  /** Override layer name for GetFeatureInfo */
  gfiLayers?: string
  /** Use WFS GetFeature POST instead of WMS GetFeatureInfo for click queries */
  gfiMode?: 'wms' | 'wfs'
  /** WFS endpoint URL (used when gfiMode='wfs') */
  wfsUrl?: string
  /** WFS type name for GetFeature (used when gfiMode='wfs') */
  wfsTypeName?: string
  /** Extra query params appended to tile and GFI URLs */
  extraParams?: string
}

/**
 * Canton WMS configs for zoning layers.
 * Each canton has one config object; aliases map all name variants to it.
 */
const WMS_AARGAU: CantonWmsConfig = {
  baseUrl: 'https://wms.geo.ag.ch/public/ch_ag_geo_are_DNPGrundnutzung/wms',
  layers: 'ch_ag_geo_are_DNPGrundnutzung',
  gfiInfoFormat: 'application/json',
}
const WMS_GENEVA: CantonWmsConfig = {
  baseUrl: 'https://geodienste.ch/db/npl_laermempfindlichkeitsstufen_v1_2_0/fra',
  layers: 'degre_de_sensibilite_au_bruit',
  gfiInfoFormat: 'text/plain',
}
const WMS_JURA: CantonWmsConfig = {
  baseUrl: 'https://geoservices.jura.ch/wms',
  layers: 'ju.sdt_02_01_zones',
  gfiMode: 'wfs',
  wfsUrl: 'https://geo.jura.ch/mapserv_proxy?ogcserver=Main_PNG',
  wfsTypeName: 'sdt_02_01_zones_geometrie_normal',
}
const WMS_LUCERNE: CantonWmsConfig = {
  baseUrl: 'https://public.geo.lu.ch/ogd/services/managed/ZONPLANX_COL_V3_MP/MapServer/WMSServer',
  layers: '1',
}
const WMS_NEUCHATEL: CantonWmsConfig = {
  baseUrl: 'https://sitn.ne.ch/services/wms',
  layers: 'at100_affectations_primaires',
}
const WMS_SOLOTHURN: CantonWmsConfig = {
  baseUrl: 'https://geo.so.ch/api/wms',
  layers: 'ch.so.arp.nutzungsplanung.grundnutzung',
  gfiInfoFormat: 'text/xml',
}
const WMS_ST_GALLEN: CantonWmsConfig = {
  baseUrl: 'https://www.geoportal.ch/services/wms',
  layers: 'v409',
  gfiInfoFormat: 'application/json',
  gfiCrs: 'EPSG:2056',
  extraParams:
    'lang=de&primaryAreaForStatistic=iggis&FILTERIDS=include&TYPE=default&getShortInformation=false',
}
const WMS_VALAIS: CantonWmsConfig = {
  baseUrl: 'https://sit.vs.ch/arcgis/services/PAZ/MapServer/WMSServer',
  layers: '0',
}
const WMS_VAUD: CantonWmsConfig = {
  baseUrl: 'https://www.ogc.vd.ch/public/services/OGC/wmsVD/Mapserver/Wmsserver',
  layers: 'vd.zone_affectation',
}
const WMS_ZUG: CantonWmsConfig = {
  baseUrl: 'https://services.geo.zg.ch/ows/nutzungsplanung',
  layers: 'zg_nutzungsplanung',
}
const WMS_ZURICH: CantonWmsConfig = {
  baseUrl: 'https://wms.zh.ch/OGDARPOerebZH',
  layers: 'nutzungsplanung-gemeinden',
  gfiInfoFormat: 'text/plain',
}

/**
 * Maps normalized canton names (all language variants) to their WMS config.
 * Keys are lowercase, accent-stripped for case-insensitive lookup.
 */
const CANTON_WMS_CONFIGS: Record<string, CantonWmsConfig> = {
  // Aargau
  aargau: WMS_AARGAU,
  argovie: WMS_AARGAU,
  argovia: WMS_AARGAU,
  // Geneva
  geneve: WMS_GENEVA,
  genf: WMS_GENEVA,
  geneva: WMS_GENEVA,
  ginevra: WMS_GENEVA,
  // Jura
  jura: WMS_JURA,
  // Lucerne
  luzern: WMS_LUCERNE,
  lucerne: WMS_LUCERNE,
  // Neuchâtel
  neuchatel: WMS_NEUCHATEL,
  neuenburg: WMS_NEUCHATEL,
  // Solothurn
  solothurn: WMS_SOLOTHURN,
  soleure: WMS_SOLOTHURN,
  // St. Gallen
  'st. gallen': WMS_ST_GALLEN,
  'saint-gall': WMS_ST_GALLEN,
  'saint gall': WMS_ST_GALLEN,
  'sankt gallen': WMS_ST_GALLEN,
  'san gallo': WMS_ST_GALLEN,
  // Valais
  valais: WMS_VALAIS,
  wallis: WMS_VALAIS,
  // Vaud
  vaud: WMS_VAUD,
  waadt: WMS_VAUD,
  // Zug
  zug: WMS_ZUG,
  zoug: WMS_ZUG,
  // Zurich
  zurich: WMS_ZURICH,
}

/** Normalize canton name for lookup (lowercase, strip accents) */
const normalizeName = (name: string): string =>
  name
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const getCantonConfig = (regionName?: string): CantonWmsConfig | undefined => {
  if (!regionName) return undefined
  return CANTON_WMS_CONFIGS[normalizeName(regionName)]
}

const GFI_FEATURE_COUNT = 1

/** Convert WGS84 lat/lon to EPSG:3857 x/y */
const toEpsg3857 = (lng: number, lat: number): [number, number] => {
  const x = (lng * 20037508.34) / 180
  const latRad = (lat * Math.PI) / 180
  const y = (Math.log(Math.tan(Math.PI / 4 + latRad / 2)) * 20037508.34) / Math.PI
  return [x, y]
}

/**
 * Convert WGS84 (lng, lat) to Swiss LV95 EPSG:2056 (E, N).
 * Uses the approximate formulas from swisstopo (good to ~1m accuracy).
 */
const toEpsg2056 = (lng: number, lat: number): [number, number] => {
  // Convert to arc-seconds then shift
  const phiPrime = (lat * 3600 - 169028.66) / 10000
  const lambdaPrime = (lng * 3600 - 26782.5) / 10000

  const phi2 = phiPrime * phiPrime
  const lambda2 = lambdaPrime * lambdaPrime

  const E =
    2600072.37 +
    211455.93 * lambdaPrime -
    10938.51 * lambdaPrime * phiPrime -
    0.36 * lambdaPrime * phi2 -
    44.54 * lambdaPrime * lambda2

  const N =
    1200147.07 +
    308807.95 * phiPrime +
    3745.25 * lambda2 +
    76.63 * phi2 -
    194.56 * lambda2 * phiPrime +
    119.79 * phi2 * phiPrime

  return [E, N]
}

/**
 * Build WFS GetFeature POST XML body with a BBOX filter around a point.
 * Uses EPSG:2056 coordinates with a small envelope (~10m).
 */
const buildWfsGetFeatureXml = (typeName: string, e: number, n: number, buffer = 10): string =>
  `<?xml version="1.0" encoding="UTF-8"?>
<wfs:GetFeature xmlns:wfs="http://www.opengis.net/wfs/2.0"
  xmlns:fes="http://www.opengis.net/fes/2.0"
  xmlns:gml="http://www.opengis.net/gml/3.2"
  service="WFS" version="2.0.0" count="1">
  <wfs:Query typeNames="${typeName}">
    <fes:Filter>
      <fes:BBOX>
        <gml:Envelope srsName="urn:ogc:def:crs:EPSG::2056">
          <gml:lowerCorner>${e - buffer} ${n - buffer}</gml:lowerCorner>
          <gml:upperCorner>${e + buffer} ${n + buffer}</gml:upperCorner>
        </gml:Envelope>
      </fes:BBOX>
    </fes:Filter>
  </wfs:Query>
</wfs:GetFeature>`

/**
 * Parse a WFS GML3 response into feature properties.
 * Extracts element values from the first <ms:*> feature member.
 */
const parseGml3Response = (xml: string): Record<string, any> | undefined => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xml, 'text/xml')

  // Find feature members — try common wrapper element names
  const member =
    doc.querySelector('member') ||
    doc.querySelector('featureMember') ||
    doc.querySelector('featureMembers')
  if (!member) return undefined

  // The actual feature element is the first child of the member
  const feature = member.children[0]
  if (!feature) return undefined

  const props: Record<string, any> = {}
  for (let i = 0; i < feature.children.length; i++) {
    const child = feature.children[i]
    // Skip geometry elements
    const localName = child.localName || child.tagName.split(':').pop() || ''
    if (
      localName === 'msGeometry' ||
      localName === 'geometry' ||
      localName === 'the_geom' ||
      localName === 'geom'
    )
      continue

    const text = child.textContent?.trim()
    if (text) props[localName] = text
  }

  return Object.keys(props).length > 0 ? props : undefined
}

/** Mapbox substitutes this token in the tiles string before each request. */
const BBOX_PLACEHOLDER = '{bbox-epsg-3857}'

const buildWmsTileUrl = (config: CantonWmsConfig): string =>
  `${config.baseUrl}?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap` +
  `&LAYERS=${config.layers}&STYLES=&CRS=EPSG:3857&BBOX=${BBOX_PLACEHOLDER}` +
  `&WIDTH=256&HEIGHT=256&FORMAT=image/png&TRANSPARENT=true` +
  (config.extraParams ? `&${config.extraParams}` : '')

/**
 * Proxied tile URL for the Mapbox raster source. The whole upstream URL is
 * URL-encoded into the `url` param, then the bbox placeholder is restored so
 * Mapbox can still substitute it (its value is digits/commas only — safe to
 * sit unencoded inside the query string the backend reads back).
 */
const buildProxiedWmsTileUrl = (config: CantonWmsConfig): string =>
  proxyWms(buildWmsTileUrl(config)).replace(encodeURIComponent(BBOX_PLACEHOLDER), BBOX_PLACEHOLDER)

/* ─── Popup helpers ─────────────────────────────────────────── */

const HIDDEN_FIELDS = new Set([
  'gid',
  'gml_id',
  'fid',
  'ogc_fid',
  'id',
  'BFS-Nr',
  'Thematik kantonal',
  'nutzungsplanung_code_gemeinde',
  // geoportal.ch internal fields
  'Identifikator',
  'Darstellungscode',
  'Gemeinde',
  'Gde Planbeschriftung',
])

/**
 * Parse WMS GetFeatureInfo text/xml response into properties.
 * Extracts Attribute elements from the first Feature in the response.
 */
const parseTextXmlResponse = (xml: string): Record<string, any> | undefined => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xml, 'text/xml')

  // Try <Attribute name="..." value="..."> pattern (QGIS Server)
  const attrs = doc.querySelectorAll('Attribute')
  if (attrs.length > 0) {
    const props: Record<string, any> = {}
    attrs.forEach((attr) => {
      const name = attr.getAttribute('name')
      const value = attr.getAttribute('value')
      if (name && value) props[name] = value
    })
    return Object.keys(props).length > 0 ? props : undefined
  }

  // Try <Feature> with child elements as key/value (MapServer / GeoServer)
  const feature = doc.querySelector('Feature, FIELDS, feature')
  if (feature) {
    const props: Record<string, any> = {}
    // Check attributes of the element itself (FIELDS pattern)
    if (feature.attributes.length > 1) {
      for (let i = 0; i < feature.attributes.length; i++) {
        const attr = feature.attributes[i]
        props[attr.name] = attr.value
      }
    } else {
      // Child elements pattern
      for (let i = 0; i < feature.children.length; i++) {
        const child = feature.children[i]
        if (child.textContent) props[child.tagName] = child.textContent
      }
    }
    return Object.keys(props).length > 0 ? props : undefined
  }

  return undefined
}

/**
 * Parse WMS GetFeatureInfo text/plain response into properties.
 * Handles common patterns like "key = value" or "key: value" per line.
 */
const parseTextPlainResponse = (text: string): Record<string, any> | undefined => {
  const props: Record<string, any> = {}
  let count = 0

  for (const line of text.split('\n')) {
    const trimmed = line.trim()
    // Match "key = value" or "key = 'value'"
    const eqMatch = trimmed.match(/^(.+?)\s*=\s*'?(.*?)'?\s*$/)
    if (eqMatch) {
      const key = eqMatch[1].trim()
      const val = eqMatch[2].trim()
      if (val && val !== 'null' && val !== 'NULL') {
        props[key] = val
        count++
      }
      continue
    }
    // Match "key: value"
    const colonMatch = trimmed.match(/^(.+?):\s+(.+)$/)
    if (colonMatch) {
      const key = colonMatch[1].trim()
      const val = colonMatch[2].trim()
      if (val && val !== 'null' && val !== 'NULL') {
        props[key] = val
        count++
      }
    }
  }

  return count > 0 ? props : undefined
}

/**
 * Parse geoportal.ch JSON response where properties is an array of {label, value}.
 * Converts to flat { key: value } for popup display.
 */
const parseArrayProperties = (
  features: any[]
): { properties: Record<string, any>; geometry: any } | undefined => {
  if (!features?.length) return undefined
  const feature = features[0]
  const propsArray = feature.properties
  if (!Array.isArray(propsArray)) return undefined

  const props: Record<string, any> = {}
  for (const item of propsArray) {
    if (item.label && item.value != null && item.value !== '') {
      props[item.label] = item.value
    }
  }

  return Object.keys(props).length > 0
    ? { properties: props, geometry: feature.geometry }
    : undefined
}

/** Title field — try multiple naming conventions */
const TITLE_KEYS = [
  'Gde Bezeichnung', // geoportal.ch — municipal zone name
  'Bezeichnung kantonal',
  'Kt Bezeichnung', // geoportal.ch — cantonal zone name
  'nutzungsplanung_bezeichnung',
  'bezeichnung',
  'Bezeichnung',
  'typ_bezeichnung',
  'genre', // Jura WFS
]

/** Try to parse a JSON string, return undefined if not valid JSON */
const tryParseJson = (val: string): any | undefined => {
  const s = String(val).trim()
  if (!(s.startsWith('{') && s.endsWith('}')) && !(s.startsWith('[') && s.endsWith(']')))
    return undefined
  try {
    return JSON.parse(s)
  } catch {
    return undefined
  }
}

/** URL field names commonly found in WMS JSON blobs */
const URL_KEYS = ['Lien', 'lien', 'Link', 'link', 'Url', 'url', 'href']
const LABEL_KEYS = ['Titre', 'titre', 'Title', 'title', 'Name', 'name', 'Abreviation']

/**
 * Render a parsed JSON value as HTML for the popup.
 * Extracts document links from arrays of objects.
 */
const formatJsonValue = (parsed: any): string => {
  // Unwrap single-key wrapper like { "Documents": [...] }
  const items = Array.isArray(parsed)
    ? parsed
    : typeof parsed === 'object' && parsed !== null
      ? (() => {
          const vals = Object.values(parsed)
          return vals.length === 1 && Array.isArray(vals[0]) ? (vals[0] as any[]) : [parsed]
        })()
      : [parsed]

  return items
    .map((item: any) => {
      if (typeof item !== 'object' || item === null) return String(item)
      const urlKey = URL_KEYS.find((k) => item[k])
      const labelKey = LABEL_KEYS.find((k) => item[k])
      if (urlKey) {
        const label = labelKey ? item[labelKey] : 'Document'
        return `<a href="${item[urlKey]}" target="_blank" rel="noopener noreferrer" style="color:#0066cc;text-decoration:underline">${label}</a>`
      }
      // No URL found — show label if present
      if (labelKey) return item[labelKey]
      return ''
    })
    .filter(Boolean)
    .join('<br/>')
}

/** Format a value for popup display — JSON parsed, URLs become links, HTML preserved */
const formatPopupValue = (val: any): string => {
  const s = String(val)
  // Try JSON first
  const parsed = tryParseJson(s)
  if (parsed !== undefined) return formatJsonValue(parsed)
  // Null → dash
  if (s === 'Null' || s === 'null' || s === 'NULL') return '—'
  // HTML anchor tags (e.g. Jura WFS returns <a href="...">...</a>) — pass through
  if (/<a\s+href=/i.test(s)) {
    return s.replace(
      /<a /g,
      '<a target="_blank" rel="noopener noreferrer" style="color:#0066cc;text-decoration:underline" '
    )
  }
  // URL detection
  if (/^https?:\/\//i.test(s)) {
    const label = s.length > 40 ? s.slice(0, 37) + '...' : s
    return `<a href="${s}" target="_blank" rel="noopener noreferrer" style="color:#0066cc;text-decoration:underline">${label}</a>`
  }
  return s
}

const buildPopupHtml = (properties: Record<string, any>): string => {
  const titleKey = TITLE_KEYS.find((k) => properties[k])
  const name = titleKey ? properties[titleKey] : 'Zone'

  const rows = Object.entries(properties)
    .filter(
      ([key, val]) => !HIDDEN_FIELDS.has(key) && val != null && val !== '' && key !== titleKey
    )
    .map(([key, val]) => {
      const displayKey = key.replace(/_/g, ' ')
      return `<tr><td style="font-weight:600;color:#666;padding:2px 8px 2px 0;white-space:nowrap;vertical-align:top">${displayKey}</td><td style="padding:2px 0">${formatPopupValue(val)}</td></tr>`
    })
    .join('')

  return `<div style="font-size:12px;max-width:280px;max-height:200px;overflow-y:auto"><div style="font-weight:700;font-size:14px;margin-bottom:6px;border-bottom:1px solid #eee;padding-bottom:4px;position:sticky;top:0;background:white">${name}</div><table style="font-size:11px">${rows}</table></div>`
}

/* ─── Component ─────────────────────────────────────────────── */

export interface ZoningMapViewerProps {
  /** WGS84 geo_center from land entity — ES geo_point format */
  geoCenter: { lat: number; lon: number } | [number, number] | string
  /** Canton name (region_name from zone) — used to look up WMS config */
  regionName?: string
  /** Land parcel polygon — if provided, shows the parcel outline instead of a red dot marker */
  landGeoPolygon?: { type: string; coordinates: number[][][] | number[][][][] }
}

/**
 * Parse ES geo_point into [lng, lat].
 * Supports: {lat, lon}, [lon, lat], "lat,lon" string.
 */
const parseGeoCenter = (
  geoCenter: { lat: number; lon: number } | [number, number] | string
): [number, number] => {
  if (Array.isArray(geoCenter)) {
    return [geoCenter[0], geoCenter[1]] // [lon, lat]
  }
  if (typeof geoCenter === 'object' && 'lat' in geoCenter) {
    return [geoCenter.lon, geoCenter.lat]
  }
  if (typeof geoCenter === 'string') {
    const parts = geoCenter.split(',')
    if (parts.length === 2) {
      // ES string format is "lat,lon"
      return [parseFloat(parts[1].trim()), parseFloat(parts[0].trim())]
    }
  }
  return [8.23, 46.82] // Switzerland fallback
}

export const ZoningMapViewer: React.FC<ZoningMapViewerProps> = ({
  geoCenter,
  regionName,
  landGeoPolygon,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const popupRef = useRef<mapboxgl.Popup | null>(null)

  // Resolve canton WMS config
  const cantonConfig = React.useMemo(() => getCantonConfig(regionName), [regionName])

  // Parse center coordinates from ES geo_point
  const [lngCenter, latCenter] = React.useMemo(() => parseGeoCenter(geoCenter), [geoCenter])

  /** Remove existing highlight layers and popup */
  const clearHighlight = useCallback((map: mapboxgl.Map) => {
    if (popupRef.current) {
      popupRef.current.remove()
      popupRef.current = null
    }
    if (map.getLayer('gfi-highlight-fill')) map.removeLayer('gfi-highlight-fill')
    if (map.getLayer('gfi-highlight-line')) map.removeLayer('gfi-highlight-line')
    if (map.getSource('gfi-highlight')) map.removeSource('gfi-highlight')
  }, [])

  /** Show properties in a popup and optionally highlight geometry */
  const showPopup = useCallback(
    (
      map: mapboxgl.Map,
      lngLat: mapboxgl.LngLat,
      properties: Record<string, any>,
      geometry?: any
    ) => {
      if (geometry) {
        map.addSource('gfi-highlight', {
          type: 'geojson',
          data: { type: 'Feature', geometry, properties },
        })
        map.addLayer({
          id: 'gfi-highlight-fill',
          type: 'fill',
          source: 'gfi-highlight',
          paint: { 'fill-color': '#0066cc', 'fill-opacity': 0.2 },
        })
        map.addLayer({
          id: 'gfi-highlight-line',
          type: 'line',
          source: 'gfi-highlight',
          paint: { 'line-color': '#0066cc', 'line-width': 2.5 },
        })
      }

      const popup = new mapboxgl.Popup({
        closeOnClick: false,
        maxWidth: '320px',
      })
        .setLngLat(lngLat)
        .setHTML(buildPopupHtml(properties))
        .addTo(map)

      popupRef.current = popup
    },
    []
  )

  /** WFS GetFeature query — used for cantons where WMS GFI is unavailable */
  const queryWfs = useCallback(
    (map: mapboxgl.Map, lngLat: mapboxgl.LngLat) => {
      if (!cantonConfig?.wfsUrl || !cantonConfig.wfsTypeName) return

      clearHighlight(map)

      const [e, n] = toEpsg2056(lngLat.lng, lngLat.lat)
      const body = buildWfsGetFeatureXml(cantonConfig.wfsTypeName, e, n)

      fetch(proxyWms(cantonConfig.wfsUrl), {
        method: 'POST',
        headers: { 'Content-Type': 'application/xml' },
        body,
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          return res.text()
        })
        .then((xml) => {
          const properties = parseGml3Response(xml)
          if (!properties) return
          showPopup(map, lngLat, properties)
        })
        .catch(() => {
          // Silent fail
        })
    },
    [cantonConfig, clearHighlight, showPopup]
  )

  // Shared GetFeatureInfo logic — used by click handler and auto-query on load
  const queryFeatureInfo = useCallback(
    (map: mapboxgl.Map, pixel: { x: number; y: number }, lngLat: mapboxgl.LngLat) => {
      if (!cantonConfig) return

      // WFS mode — delegate to WFS query
      if (cantonConfig.gfiMode === 'wfs') {
        queryWfs(map, lngLat)
        return
      }

      clearHighlight(map)

      const canvas = map.getCanvas()
      const width = canvas.clientWidth
      const height = canvas.clientHeight
      const bounds = map.getBounds()
      if (!bounds) return

      const gfiCrs = cantonConfig.gfiCrs || 'EPSG:4326'
      let bbox: string
      if (gfiCrs === 'EPSG:2056') {
        const sw = toEpsg2056(bounds.getWest(), bounds.getSouth())
        const ne = toEpsg2056(bounds.getEast(), bounds.getNorth())
        bbox = `${sw[0]},${sw[1]},${ne[0]},${ne[1]}`
      } else if (gfiCrs === 'EPSG:3857') {
        const sw = toEpsg3857(bounds.getWest(), bounds.getSouth())
        const ne = toEpsg3857(bounds.getEast(), bounds.getNorth())
        bbox = `${sw[0]},${sw[1]},${ne[0]},${ne[1]}`
      } else {
        // EPSG:4326 — WMS 1.3.0 axis order: minLat,minLon,maxLat,maxLon
        bbox = `${bounds.getSouth()},${bounds.getWest()},${bounds.getNorth()},${bounds.getEast()}`
      }

      const i = Math.round(pixel.x)
      const j = Math.round(pixel.y)

      const gfiBase = cantonConfig.gfiBaseUrl || cantonConfig.baseUrl
      const gfiLayer = cantonConfig.gfiLayers || cantonConfig.layers
      const infoFormat = cantonConfig.gfiInfoFormat || 'application/geo+json'
      const encodedFormat = encodeURIComponent(infoFormat)

      const url =
        `${gfiBase}?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetFeatureInfo` +
        `&LAYERS=${gfiLayer}&QUERY_LAYERS=${gfiLayer}` +
        `&STYLES=&CRS=${gfiCrs}&BBOX=${bbox}&WIDTH=${width}&HEIGHT=${height}` +
        `&I=${i}&J=${j}` +
        `&INFO_FORMAT=${encodedFormat}&FEATURE_COUNT=${GFI_FEATURE_COUNT}` +
        (cantonConfig.extraParams ? `&${cantonConfig.extraParams}` : '')

      fetch(proxyWms(url))
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          return infoFormat === 'text/plain' || infoFormat === 'text/xml' ? res.text() : res.json()
        })
        .then((data) => {
          let properties: Record<string, any> | undefined
          let geometry: any

          if (infoFormat === 'text/xml') {
            properties = parseTextXmlResponse(data as string)
          } else if (infoFormat === 'text/plain') {
            properties = parseTextPlainResponse(data as string)
          } else {
            // GeoJSON or JSON response
            const geojson = data as any
            if (!geojson?.features?.length) return

            // Detect geoportal.ch array-of-objects format
            const firstFeature = geojson.features[0]
            if (Array.isArray(firstFeature?.properties)) {
              const parsed = parseArrayProperties(geojson.features)
              if (!parsed) return
              properties = parsed.properties
              geometry = parsed.geometry
            } else {
              // Standard GeoJSON properties
              const withGeom = geojson.features.find((f: any) => f.geometry)
              const grundnutzung = geojson.features.find(
                (f: any) => f.id && /grundnutzung|zone|wohn|arbeit|kern|industrie/i.test(f.id)
              )
              const feature = withGeom || grundnutzung || geojson.features[0]

              properties = feature.properties
              geometry = feature.geometry
            }
          }

          if (!properties) return
          showPopup(map, lngLat, properties, geometry)
        })
        .catch(() => {
          // Silent fail — map remains usable for visual inspection
        })
    },
    [cantonConfig, clearHighlight, showPopup, queryWfs]
  )

  // Click handler — delegates to shared queryFeatureInfo
  const handleMapClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      const map = mapRef.current
      if (!map) return
      queryFeatureInfo(map, e.point, e.lngLat)
    },
    [queryFeatureInfo]
  )

  // Initialize map (runs once on mount; key= prop handles coordinate changes)
  useEffect(() => {
    if (!containerRef.current || !cantonConfig) return

    mapboxgl.accessToken = MAPBOX_TOKEN

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [lngCenter, latCenter],
      zoom: 15,
      attributionControl: false,
    })

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')

    mapRef.current = map

    map.on('load', () => {
      // WMS raster tiles
      map.addSource('wms-zoning', {
        type: 'raster',
        tiles: [buildProxiedWmsTileUrl(cantonConfig)],
        tileSize: 256,
      })
      map.addLayer({
        id: 'wms-zoning-layer',
        type: 'raster',
        source: 'wms-zoning',
        paint: { 'raster-opacity': 0.85 },
      })

      // Land parcel — polygon outline or fallback to dot marker
      if (landGeoPolygon?.coordinates) {
        map.addSource('land-parcel', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: landGeoPolygon.type as 'Polygon' | 'MultiPolygon',
              coordinates: landGeoPolygon.coordinates as any,
            },
          },
        })
        map.addLayer({
          id: 'land-parcel-fill',
          type: 'fill',
          source: 'land-parcel',
          paint: { 'fill-color': '#0294df', 'fill-opacity': 0.15 },
        })
        map.addLayer({
          id: 'land-parcel-outline',
          type: 'line',
          source: 'land-parcel',
          paint: { 'line-color': '#0088BB', 'line-width': 2.5 },
        })
      } else {
        const markerEl = document.createElement('div')
        markerEl.style.cssText =
          'width:14px;height:14px;background:#ff3b30;border-radius:50%;border:2.5px solid white;box-shadow:0 1px 6px rgba(0,0,0,0.4);'
        new mapboxgl.Marker({ element: markerEl }).setLngLat([lngCenter, latCenter]).addTo(map)
      }

      // Click handler
      map.on('click', handleMapClick)

      // Cursor on map (indicate clickability)
      map.getCanvas().style.cursor = 'crosshair'

      // Auto-query at land center after map viewport has fully settled
      // (wait for 'idle' so container dimensions and bounds are final)
      map.once('idle', () => {
        const centerPixel = map.project([lngCenter, latCenter])
        queryFeatureInfo(map, centerPixel, new mapboxgl.LngLat(lngCenter, latCenter))
      })
    })

    return () => {
      map.remove()
      mapRef.current = null
      popupRef.current = null
    }
  }, []) // Safe: key={centerChProjection} forces full remount on coordinate change

  if (!cantonConfig) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          minHeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: 14,
        }}
      >
        Geoportal map not available for this canton
      </div>
    )
  }

  return <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight: 500 }} />
}
